/**
 * Visitor attachment upload.
 *
 *   POST /api/v1/webchat/widgets/:widgetId/visitors/:visitorId/attachments
 *   Authorization: Bearer <sessionToken from handshake>
 *   multipart/form-data with a single `file` part
 *
 * The response is a **receipt**, not a URL: the storage location never reaches
 * the browser. The visitor sends the receipt back with their message and the
 * server resolves it — which is what stops a message from pointing at a file
 * its sender did not upload. See mirlo-messages-service
 * `webchat-webhook/utils/attachment-token.ts`.
 *
 * Uploading is only offered when the handshake advertised
 * `config.attachments`. That is a hint for the UI, not a guarantee: the server
 * refuses independently, so every rejection here is expected to be handled.
 */

import type { MediaKind } from './api-types';

export interface UploadedAttachment {
  attachmentToken: string;
  kind: MediaKind;
  name?: string;
  size: number;
}

/** Reasons an upload can fail, as sent by the server. */
export type AttachmentUploadCode =
  | 'attachments_not_enabled'
  | 'mime_type_not_allowed'
  | 'file_too_large'
  | 'rate_limit_exceeded'
  | 'network_error'
  | 'upload_failed';

export class AttachmentUploadError extends Error {
  constructor(
    public readonly code: AttachmentUploadCode,
    public readonly status: number,
  ) {
    super(`Attachment upload failed (${status}): ${code}`);
    this.name = 'AttachmentUploadError';
  }
}

const KNOWN_CODES: AttachmentUploadCode[] = [
  'attachments_not_enabled',
  'mime_type_not_allowed',
  'file_too_large',
  'rate_limit_exceeded',
];

export async function uploadAttachment(
  apiBaseUrl: string,
  widgetId: string,
  visitorId: string,
  sessionToken: string,
  file: File,
): Promise<UploadedAttachment> {
  const url = `${apiBaseUrl.replace(/\/+$/, '')}/api/v1/webchat/widgets/${encodeURIComponent(
    widgetId,
  )}/visitors/${encodeURIComponent(visitorId)}/attachments`;

  const body = new FormData();
  body.append('file', file);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      // Only Authorization. Setting Content-Type by hand would drop the
      // multipart boundary the browser generates, and the server could not
      // parse the body.
      headers: { Authorization: `Bearer ${sessionToken}` },
      body,
    });
  } catch {
    throw new AttachmentUploadError('network_error', 0);
  }

  if (!response.ok) {
    let code: AttachmentUploadCode = 'upload_failed';
    try {
      const parsed = (await response.json()) as { message?: string };
      if (KNOWN_CODES.includes(parsed.message as AttachmentUploadCode)) {
        code = parsed.message as AttachmentUploadCode;
      }
    } catch {
      // Body was not JSON — keep the generic code.
    }
    throw new AttachmentUploadError(code, response.status);
  }

  return (await response.json()) as UploadedAttachment;
}
